/* ==========================================================================
   Aroma Quiz Premium: engine
   Reads window.aromaQuizData[section.id] (built server-side in sc-quiz-v2.liquid)
   and drives
   a self-contained: intro -> question x N -> analyzing -> result flow.

   Scoring model
   -------------
   1) Each option in Question 1 carries a scent-FAMILY slug in its `tag`
      (floral | fresh | oriental | woody). The family with the most votes
      wins. Ties are broken by which family has more matching products in
      the configured collection.
   2) Every option in every question can also carry one or more free-form,
      comma-separated tags (e.g. "evening-events, romantic-soft"). All
      selected tags across the whole quiz are pooled.
   3) Within the winning family, each product is scored by how many of its
      Shopify tags intersect the pooled quiz tags. Highest score wins;
      ties keep collection order. The highest-scoring product is the
      "Best Match"; the next ones are shown as additional recommendations.
   4) If the winning family doesn't have enough products to fill the
      configured number of recommendation slots, the best-scoring oils from
      the OTHER families are appended (labelled with the family they come
      from) so the customer always sees a full set of options rather than a
      single lonely card. Toggle in the theme editor.
   5) Diffuser blocks are matched the same way against the pooled tags, so
      a "Bold" / "Complex" intensity answer naturally pulls a
      higher-capacity diffuser if the merchant tagged the blocks that way.
      The remaining diffusers are shown underneath as alternatives, in a
      2-column grid. Every diffuser card carries a "Why this one" line built
      from the answers whose tags it matched, falling back to the block's own
      written reason when nothing matched.

   Gift oil bundle
   ---------------
   The gift offer lives in exactly ONE place: the bundle block at the bottom of
   the result. The diffuser cards say nothing about it -- no picker, no ribbon,
   no value line. Spread across every qualifying card the offer read as
   decoration and left no way to tell which pick counted; gathered into one
   block it reads as an event the customer is being invited to.

   The block builds itself from the section's existing diffuser and gift oil
   blocks and needs nothing switched on. A diffuser is in the offer unless its
   block is ticked "Hide from the gift oil bundle".

   The bundle is a two-step opt-in -- pick a qualifying diffuser, pick a 150ml
   oil -- with a summary bar that adds both to the cart in one go. The oil goes
   in as an ordinary line item tagged with cart line-item properties so it's
   identifiable in the order. Making it cost nothing is a Shopify discount job
   (automatic Buy X Get Y, or the optional discount code setting) -- the
   storefront cannot set a line price to zero on its own.
   ========================================================================== */

(function () {
  "use strict";

  function init(root, data) {
    if (!data || !Array.isArray(data.questions) || !data.questions.length) {
      // Previously this just returned here, which left the Start button
      // sitting on the page with no click handler at all -- clicking it did
      // nothing, with no error anywhere to explain why. That happens when
      // this section's data failed to parse (a JS error in the inline data
      // script further down the page) or when this section instance has no
      // "Question" blocks configured yet. Fail loudly instead: warn in the
      // console and show an inline notice so it's obvious the section isn't
      // wired up, rather than looking like a dead button.
      if (window.console && console.warn) {
        console.warn(
          "[Aroma Quiz] Could not start (section " + (root.getAttribute("data-section-id") || "?") + "): its data is " +
          (data ? "missing its questions array" : "missing entirely") +
          ". Check that this section has Question blocks added in the " +
          "theme editor, and that no other script on the page threw before " +
          "window.aromaQuizData was populated."
        );
      }
      var introCard = root.querySelector(".aq-intro-card");
      if (introCard && !introCard.querySelector(".aq-config-warning")) {
        var warn = document.createElement("p");
        warn.className = "aq-config-warning";
        warn.textContent = "This quiz isn't finished setting up yet (no questions found). Add Question blocks to this section in the theme editor.";
        introCard.appendChild(warn);
      }
      return;
    }

    var state = {
      index: 0,
      answers: {} // questionIndex -> array of option objects
    };

    var els = {
      progressWrap: root.querySelector("[data-aq-progress]"),
      screens: {
        intro: root.querySelector('[data-aq-screen="intro"]'),
        question: root.querySelector('[data-aq-screen="question"]'),
        loading: root.querySelector('[data-aq-screen="loading"]'),
        result: root.querySelector('[data-aq-screen="result"]')
      },
      questionInner: root.querySelector("#aq-question-inner"),
      resultInner: root.querySelector("#aq-result-inner"),
      nav: root.querySelector(".aq-nav"),
      backBtn: root.querySelector(".aq-back-btn"),
      continueBtn: root.querySelector(".aq-continue-btn"),
      startBtn: root.querySelector(".aq-start-btn"),
      loadingWrap: root.querySelector(".aq-loading-visual"),
      loadingImgA: root.querySelector(".aq-loading-img-a"),
      loadingImgB: root.querySelector(".aq-loading-img-b"),
      loadingText: root.querySelector(".aq-loading-text"),
      loadingBarFill: root.querySelector(".aq-loading-bar span")
    };

    buildProgress();
    bindNav();

    function showScreen(name) {
      Object.keys(els.screens).forEach(function (key) {
        var el = els.screens[key];
        if (!el) return;
        el.hidden = key !== name;
      });
      if (els.progressWrap) els.progressWrap.hidden = name !== "question";
      root.dispatchEvent(new CustomEvent("aroma-quiz:screen", { detail: { screen: name } }));
    }

    function buildProgress() {
      if (!els.progressWrap) return;
      var stepsWrap = els.progressWrap.querySelector(".aq-steps");
      var line = document.createElement("div");
      line.className = "aq-progress-line";
      var fill = document.createElement("div");
      fill.className = "aq-progress-fill";
      line.appendChild(fill);
      stepsWrap.innerHTML = "";
      stepsWrap.appendChild(line);

      data.questions.forEach(function (q, i) {
        var step = document.createElement("div");
        step.className = "aq-step";
        step.dataset.step = i + 1;
        step.innerHTML =
          '<span class="aq-step-num">' + (i + 1) + "</span>" +
          '<svg class="aq-step-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>';
        stepsWrap.appendChild(step);
      });

      els.progressWrap.__fill = fill;
      updateProgress();
    }

    function updateProgress() {
      if (!els.progressWrap) return;
      var total = data.questions.length;
      var pct = total > 1 ? (state.index / (total - 1)) * 100 : 0;
      if (els.progressWrap.__fill) els.progressWrap.__fill.style.width = Math.min(pct, 100) + "%";

      var steps = els.progressWrap.querySelectorAll(".aq-step");
      steps.forEach(function (step, i) {
        step.classList.toggle("is-active", i === state.index);
        step.classList.toggle("is-done", i < state.index);
      });

      var label = els.progressWrap.querySelector("#aq-step-current");
      if (label) label.textContent = state.index + 1;
    }

    function bindNav() {
      if (els.startBtn) {
        els.startBtn.addEventListener("click", function () {
          state.index = 0;
          renderQuestion();
          showScreen("question");
        });
      }
      if (els.backBtn) {
        els.backBtn.addEventListener("click", function () {
          if (state.index === 0) return;
          state.index -= 1;
          renderQuestion();
          scrollToSectionTop();
        });
      }
      if (els.continueBtn) {
        els.continueBtn.addEventListener("click", function () {
          goNext();
        });
      }
    }

    // Measures anything the theme has pinned to the top of the window -- a
    // sticky or fixed nav bar, which nearly every Shopify theme has -- so we
    // never scroll the top of the quiz underneath it. Measured live rather
    // than hard-coded, because the bar's height changes between desktop and
    // mobile, and between themes.
    function stickyTopOffset() {
      var offset = 0;
      try {
        var probes = document.elementsFromPoint(window.innerWidth / 2, 2) || [];
        for (var i = 0; i < probes.length; i++) {
          var el = probes[i];
          if (el === document.body || el === document.documentElement) continue;
          var pos = window.getComputedStyle(el).position;
          if (pos !== "fixed" && pos !== "sticky") continue;
          var rect = el.getBoundingClientRect();
          // Skip full-height overlays (drawers, modals); we only want top bars.
          if (rect.height > window.innerHeight / 2) continue;
          if (rect.bottom > offset) offset = rect.bottom;
        }
      } catch (e) {}
      return offset;
    }

    // Puts the TOP OF THE WHOLE QUIZ in view -- heading and subheading
    // included -- clear of any sticky theme header.
    //
    // This previously scrolled to the progress bar instead, which sits BELOW
    // the heading block. Aligning the progress bar with the top of the
    // viewport pushed the heading and subheading off-screen above, so they
    // vanished from question 2 onward (question 1 looked fine only because
    // no scroll had run yet). Scrolling to the section root, minus the
    // sticky nav's height, keeps the heading visible on every screen.
    function scrollToSectionTop() {
      if (!root) return;

      // The section is styled `overflow-x: clip`, but older browsers fall
      // back to `overflow: hidden`, which makes the section a scroll
      // container -- and any scrollIntoView() call, from this script or from
      // the browser's own focus handling, can scroll the heading up out of
      // the clipped box where it becomes invisible. Force it back to the top
      // so the heading is always inside the visible area.
      if (root.scrollTop) root.scrollTop = 0;

      var top = root.getBoundingClientRect().top + window.pageYOffset - stickyTopOffset() - 8;
      if (top < 0) top = 0;
      if (typeof window.scrollTo === "function") {
        try {
          window.scrollTo({ top: top, behavior: "smooth" });
        } catch (e) {
          window.scrollTo(0, top);
        }
      }
    }

    function renderQuestion() {
      var q = data.questions[state.index];
      if (!q) return;
      updateProgress();

      if (els.backBtn) els.backBtn.hidden = state.index === 0;
      if (els.nav) els.nav.classList.toggle("is-single-action", state.index === 0);

      var frag = document.createElement("div");
      frag.className = "aq-question-card";

      var title = document.createElement("h3");
      title.className = "aq-question-title";
      title.textContent = q.title || "";
      frag.appendChild(title);

      var hint = document.createElement("p");
      hint.className = "aq-question-hint";
      hint.textContent = q.multi ? "Select up to 2" : "Select one";
      frag.appendChild(hint);

      var grid = document.createElement("div");
      grid.className = "aq-options";
      grid.setAttribute("data-count", String((q.options || []).length));
      grid.setAttribute("role", q.multi ? "group" : "radiogroup");

      var selected = state.answers[state.index] || [];

      (q.options || []).forEach(function (opt, i) {
        var card = document.createElement("button");
        card.type = "button";
        card.className = "aq-option";
        card.setAttribute("role", q.multi ? "checkbox" : "radio");
        var isSelected = selected.indexOf(i) !== -1;
        card.setAttribute("aria-checked", isSelected ? "true" : "false");
        if (isSelected) card.classList.add("is-selected");

        var media = document.createElement("div");
        media.className = "aq-option-media";
        if (opt.image) {
          var img = document.createElement("img");
          img.src = opt.image;
          img.alt = opt.text || "";
          img.loading = "lazy";
          media.appendChild(img);
        } else {
          var empty = document.createElement("div");
          empty.className = "aq-option-media-empty";
          empty.textContent = (opt.text || "?").charAt(0);
          media.appendChild(empty);
        }
        var badge = document.createElement("span");
        badge.className = "aq-option-badge";
        badge.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>';
        media.appendChild(badge);
        card.appendChild(media);

        var body = document.createElement("div");
        body.className = "aq-option-body";
        var label = document.createElement("span");
        label.className = "aq-option-label";
        label.textContent = opt.text || "";
        body.appendChild(label);
        if (opt.description) {
          var desc = document.createElement("span");
          desc.className = "aq-option-desc";
          desc.textContent = opt.description;
          body.appendChild(desc);
        }
        card.appendChild(body);

        card.addEventListener("click", function () {
          selectOption(q, i, card, grid);
        });

        grid.appendChild(card);
      });

      frag.appendChild(grid);
      els.questionInner.innerHTML = "";
      els.questionInner.appendChild(frag);

      updateContinueVisibility(q);
    }

    function selectOption(q, i, card, grid) {
      var current = state.answers[state.index] || [];

      if (q.multi) {
        var pos = current.indexOf(i);
        if (pos !== -1) {
          current.splice(pos, 1);
        } else {
          current.push(i);
          if (current.length > 2) current.shift(); // cap at 2 for clean scoring
        }
        state.answers[state.index] = current;
        syncSelectedUI(grid, current);
        updateContinueVisibility(q);
      } else {
        state.answers[state.index] = [i];
        syncSelectedUI(grid, [i]);
        updateContinueVisibility(q);
      }
    }

    function syncSelectedUI(grid, selectedArr) {
      Array.prototype.forEach.call(grid.children, function (card, idx) {
        var isSel = selectedArr.indexOf(idx) !== -1;
        card.classList.toggle("is-selected", isSel);
        card.setAttribute("aria-checked", isSel ? "true" : "false");
      });
    }

    function updateContinueVisibility(q) {
      if (!els.continueBtn) return;
      var has = (state.answers[state.index] || []).length > 0;
      els.continueBtn.hidden = false;
      els.continueBtn.disabled = !has;
    }

    function goNext() {
      var has = (state.answers[state.index] || []).length > 0;
      if (!has) return;
      if (state.index < data.questions.length - 1) {
        state.index += 1;
        renderQuestion();
        scrollToSectionTop();
      } else {
        finish();
      }
    }

    function finish() {
      var results = computeResults();
      scrollToSectionTop();
      runLoadingSequence(results, function () {
        renderResult(results);
        showScreen("result");
        scrollToSectionTop();
      });
    }

    // ---------------------------------------------------------------------
    // Scoring
    // ---------------------------------------------------------------------
    function collectSelectedTags() {
      var tags = [];
      var familyVotes = {};
      var tagLabels = {};

      data.questions.forEach(function (q, qi) {
        var picks = state.answers[qi] || [];
        picks.forEach(function (optIndex) {
          var opt = q.options[optIndex];
          if (!opt || !opt.tag) return;
          opt.tag.split(",").forEach(function (t) {
            var clean = t.trim().toLowerCase();
            if (!clean) return;
            tags.push(clean);
            // Keep the human label the customer actually clicked, keyed by the
            // tag it carried. The result screen uses this to explain a
            // recommendation ("Matched to your \u201CBold\u201D answer")
            // instead of leaking internal slugs like "bold, evening-events".
            if (!tagLabels[clean]) tagLabels[clean] = opt.text || clean;
            if (isFamily(clean)) {
              familyVotes[clean] = (familyVotes[clean] || 0) + 1;
            }
          });
        });
      });

      return { tags: tags, familyVotes: familyVotes, tagLabels: tagLabels };
    }

    function isFamily(slug) {
      return data.families && Object.prototype.hasOwnProperty.call(data.families, slug);
    }

    function pickTopFamily(familyVotes) {
      var families = Object.keys(data.families || {});
      if (!families.length) return null;

      var best = null, bestScore = -1;
      families.forEach(function (fam) {
        var score = familyVotes[fam] || 0;
        var productCount = (data.oils || []).filter(function (p) {
          return productFamily(p) === fam;
        }).length;
        // primary sort: vote score. tie-break: more available products.
        if (score > bestScore || (score === bestScore && productCount > (best ? best.productCount : -1))) {
          best = { key: fam, productCount: productCount };
          bestScore = score;
        }
      });
      return best ? best.key : families[0];
    }

    function productFamily(product) {
      var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var families = Object.keys(data.families || {});
      for (var i = 0; i < families.length; i++) {
        if (tags.indexOf(families[i]) !== -1) return families[i];
      }
      return null;
    }

    function scoreProductAgainstTags(product, tags) {
      var pTags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var score = 0;
      tags.forEach(function (t) {
        if (pTags.indexOf(t) !== -1) score += 1;
      });
      return score;
    }

    var DEFAULT_RESULT_COUNT = 4;

    function byScoreThenOrder(a, b) {
      return (b.score - a.score) || (a.order - b.order);
    }

    function computeResults() {
      var picked = collectSelectedTags();
      var topFamily = pickTopFamily(picked.familyVotes);

      var scored = (data.oils || []).map(function (p, idx) {
        return {
          product: p,
          family: productFamily(p),
          score: scoreProductAgainstTags(p, picked.tags),
          order: idx
        };
      });

      var inFamily = scored.filter(function (s) { return s.family === topFamily; }).sort(byScoreThenOrder);
      var outFamily = scored.filter(function (s) { return s.family !== topFamily; }).sort(byScoreThenOrder);

      var wanted = Math.max(1, Number(data.resultCount) || DEFAULT_RESULT_COUNT);

      // Best match always comes from the winning family when that family has
      // any products at all; the remaining slots are topped up from the other
      // families so the result screen shows a full shelf instead of one card.
      var picks = inFamily.slice(0, wanted);
      if (picks.length < wanted && data.fillOtherFamilies !== false) {
        picks = picks.concat(outFamily.slice(0, wanted - picks.length));
      }
      if (!picks.length) picks = scored.slice(0, wanted);

      var oils = picks.map(function (entry, i) {
        return {
          product: entry.product,
          family: entry.family,
          isTop: i === 0,
          crossFamily: i > 0 && entry.family !== topFamily
        };
      });

      // ---- Diffusers: best match + the rest as alternatives --------------
      var diffScored = (data.diffusers || [])
        .filter(function (d) { return d && d.product; })
        .map(function (d, idx) {
          var dTags = (d.tag || "").split(",").map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
          var s = 0;
          var matched = [];
          dTags.forEach(function (t) {
            if (picked.tags.indexOf(t) === -1) return;
            s += 1;
            var label = picked.tagLabels[t];
            if (label && matched.indexOf(label) === -1) matched.push(label);
          });
          return { block: d, score: s, order: idx, matched: matched };
        })
        .sort(byScoreThenOrder);

      // Entries, not bare blocks: `matched` has to reach diffuserCardHtml so
      // each card can explain itself.
      var bestDiffuser = diffScored.length ? diffScored[0] : null;
      var altLimit = data.showAltDiffusers === false ? 0 : Math.max(0, Number(data.altDiffuserCount) || 0);
      var altDiffusers = diffScored.slice(1, 1 + altLimit);

      return {
        family: topFamily,
        oils: oils,
        diffuser: bestDiffuser,
        altDiffusers: altDiffusers
      };
    }

    // ---------------------------------------------------------------------
    // Loading / analyzing transition
    // ---------------------------------------------------------------------
    var LOADING_DURATION_MS = 12500;
    var IMAGE_CYCLE_MS = 4200;

    function runLoadingSequence(results, cb) {
      showScreen("loading");
      var fam = results.family && data.families[results.family];
      var images = (fam && fam.loadingImages && fam.loadingImages.length)
        ? fam.loadingImages
        : (fam && fam.loading ? [fam.loading] : []);

      if (els.loadingWrap) {
        els.loadingWrap.style.setProperty("--smoke-color", (fam && fam.tint) || "var(--quiz-accent)");
        els.loadingWrap.style.display = images.length ? "" : "none";
      }

      var cycleTimer = null;
      if (els.loadingImgA && els.loadingImgB && images.length) {
        els.loadingImgA.src = images[0];
        els.loadingImgA.classList.add("is-active");
        els.loadingImgB.classList.remove("is-active");
        if (images.length > 1) {
          els.loadingImgB.src = images[1];
          var showingA = true;
          cycleTimer = window.setInterval(function () {
            showingA = !showingA;
            els.loadingImgA.classList.toggle("is-active", showingA);
            els.loadingImgB.classList.toggle("is-active", !showingA);
          }, IMAGE_CYCLE_MS);
        }
      }

      var messages = [
        "Reading your answers...",
        "Blending botanical notes...",
        (fam ? "Leaning into " + fam.name + "..." : "Finding your family..."),
        "Letting it breathe...",
        "Finalizing your signature scent..."
      ];
      var i = 0;
      if (els.loadingText) els.loadingText.textContent = messages[0];
      if (els.loadingBarFill) {
        els.loadingBarFill.style.transitionDuration = LOADING_DURATION_MS + "ms";
        els.loadingBarFill.style.width = "0%";
        window.requestAnimationFrame(function () {
          els.loadingBarFill.style.width = "100%";
        });
      }

      var step = Math.round(LOADING_DURATION_MS / messages.length);
      var interval = window.setInterval(function () {
        i += 1;
        if (i < messages.length && els.loadingText) els.loadingText.textContent = messages[i];
      }, step);

      window.setTimeout(function () {
        window.clearInterval(interval);
        if (cycleTimer) window.clearInterval(cycleTimer);
        cb();
      }, LOADING_DURATION_MS);
    }

    // ---------------------------------------------------------------------
    // Result rendering
    // ---------------------------------------------------------------------
    var bundle = { diffusers: [], oils: [], d: null, o: null };

    function availableGifts() {
      if (!data.gift || data.gift.enabled === false) return [];
      return (data.gifts || []).filter(function (g) { return g && g.variantId && g.available !== false; });
    }

    function renderResult(results) {
      var fam = results.family && data.families[results.family];
      var copy = data.copy || {};

      var html = "";

      html += '<div class="aq-result-hero">';
      if (fam && fam.hero) html += '<img src="' + esc(fam.hero) + '" alt="' + esc(fam.name) + '">';
      html += '<div class="aq-result-hero-body">';
      html += '<p class="aq-result-eyebrow">' + esc(copy.resultHeading || "Your Signature Scent") + "</p>";
      html += '<h3 class="aq-result-family-name">' + esc(fam ? fam.name : "") + "</h3>";
      if (fam && fam.story) html += '<p class="aq-result-story">' + esc(fam.story) + "</p>";
      html += "</div></div>";

      html += '<h4 class="aq-result-section-title">' + esc(copy.resultSectionTitle || "Recommended For You") + "</h4>";
      html += '<p class="aq-result-section-sub">' + esc(copy.resultSectionSub || "Your closest match first, plus other oils that suit your answers") + "</p>";

      if (results.oils && results.oils.length) {
        html += '<div class="aq-oils-grid">';
        results.oils.forEach(function (entry) {
          html += oilCardHtml(entry, copy);
        });
        html += "</div>";
      } else {
        html += '<div class="aq-empty-note">' + esc("Oil recommendations will appear here once products are connected in the theme editor.") + "</div>";
      }

      html += '<div class="aq-diffuser-section">';
      html += '<h4 class="aq-result-section-title">Complete Your Ritual</h4>';
      html += '<p class="aq-result-section-sub">Paired separately based on the intensity you chose</p>';
      html += diffuserCardHtml(results.diffuser, copy, true);

      if (results.altDiffusers && results.altDiffusers.length) {
        html += '<div class="aq-diffuser-alts">';
        html += '<p class="aq-diffuser-alts-title">' + esc(copy.altDiffuserTitle || "Other Diffusers Worth Considering") + "</p>";
        html += '<div class="aq-diffuser-alts-grid">';
        results.altDiffusers.forEach(function (entry) {
          html += diffuserCardHtml(entry, copy, false);
        });
        html += "</div></div>";
      }
      html += "</div>";

      html += bundleHtml(results, copy);

      html += '<div class="aq-result-actions">';
      html += '<button type="button" class="aq-btn aq-btn-ghost" data-aq-retake>' + esc(copy.retakeButton || "Retake the Quiz") + "</button>";
      if (data.collectionUrl) {
        html += '<a class="aq-btn aq-btn-ghost" href="' + esc(data.collectionUrl) + '">' + esc(copy.shopCollection || "Shop All Aroma Oils") + "</a>";
      }
      html += "</div>";

      els.resultInner.innerHTML = html;

      var retakeBtn = els.resultInner.querySelector("[data-aq-retake]");
      if (retakeBtn) {
        retakeBtn.addEventListener("click", function () {
          state.index = 0;
          state.answers = {};
          renderQuestion();
          showScreen("intro");
          scrollToSectionTop();
        });
      }

      // Size pills on the oil cards
      Array.prototype.forEach.call(els.resultInner.querySelectorAll("[data-aq-card]"), function (card, cardIndex) {
        var entry = results.oils[cardIndex];
        if (!entry) return;
        var oil = entry.product;
        var variants = (oil.variants && oil.variants.length) ? oil.variants : [{ label: "", price: oil.price, variantId: oil.variantId, available: oil.available }];
        var pills = card.querySelectorAll(".aq-size-pill");
        Array.prototype.forEach.call(pills, function (pill) {
          pill.addEventListener("click", function () {
            Array.prototype.forEach.call(pills, function (p) { p.classList.remove("is-selected"); });
            pill.classList.add("is-selected");
            var v = variants[Number(pill.getAttribute("data-variant-index"))];
            if (!v) return;
            var priceEl = card.querySelector(".aq-product-price");
            var addBtn = card.querySelector("[data-aq-add-single]");
            if (priceEl) priceEl.innerHTML = v.price || "";
            if (addBtn) {
              addBtn.setAttribute("data-aq-add-single", v.variantId || "");
              addBtn.disabled = !v.available;
            }
          });
        });
      });

      // Gift oil bundle block
      wireBundle(copy);

      // Single-product add to cart (oils)
      Array.prototype.forEach.call(els.resultInner.querySelectorAll("[data-aq-add-single]"), function (btn) {
        btn.addEventListener("click", function () {
          var variantId = btn.getAttribute("data-aq-add-single");
          if (!variantId) return;
          addItemsToCart([{ id: Number(variantId), quantity: 1 }], btn);
        });
      });

      // Diffuser add to cart. The gift oil is NOT attached here -- a customer
      // who wants it goes through the bundle block, which is the one place
      // that knows which oil they picked.
      Array.prototype.forEach.call(els.resultInner.querySelectorAll("[data-aq-add-diffuser]"), function (btn) {
        btn.addEventListener("click", function () {
          var variantId = btn.getAttribute("data-aq-add-diffuser");
          if (!variantId) return;
          addItemsToCart([{ id: Number(variantId), quantity: 1 }], btn);
        });
      });
    }

    function placeholderMediaHtml() {
      return (
        '<div class="aq-product-media-placeholder">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
        '<path d="M9 3h6l1 4H8l1-4Z"/><path d="M7 7h10l1.2 12.2A2 2 0 0 1 16.2 21H7.8a2 2 0 0 1-2-1.8L7 7Z"/>' +
        '<path d="M9.5 12c1 1.2 1 2.3 0 3.5M14.5 12c1 1.2 1 2.3 0 3.5" opacity="0.6"/>' +
        "</svg></div>"
      );
    }

    function giftSvg(size) {
      var s = size || 16;
      return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/>' +
        '<path d="M12 8S10.6 3 8.2 3a2.4 2.4 0 0 0 0 5H12Z"/><path d="M12 8s1.4-5 3.8-5a2.4 2.4 0 0 1 0 5H12Z"/>' +
        "</svg>";
    }

    function joinLabels(list) {
      if (list.length === 1) return list[0];
      if (list.length === 2) return list[0] + " and " + list[1];
      return list.slice(0, -1).join(", ") + " and " + list[list.length - 1];
    }

    // Why this diffuser, in the customer's own words where possible. Falls back
    // to the merchant's written reason (then the short note) when the customer's
    // answers didn't match this block's tags -- which is normal for the
    // alternatives, since only the best match scored on every tag.
    function diffuserReasonText(entry, copy) {
      var labels = (entry.matched || []).filter(Boolean);
      if (labels.length) {
        var prefix = (copy && copy.reasonPrefix) || "Matched to your";
        var quoted = labels.map(function (l) { return "\u201C" + l + "\u201D"; });
        return prefix + " " + joinLabels(quoted) + (labels.length > 1 ? " answers." : " answer.");
      }
      return entry.block.reason || entry.block.note || "";
    }

    function checkSvg(size) {
      var s = size || 14;
      return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>';
    }

    function oilCardHtml(entry, copy) {
      var oil = entry.product;
      var isTopPick = entry.isTop;
      var variants = (oil.variants && oil.variants.length) ? oil.variants : [{ label: "", price: oil.price, variantId: oil.variantId, available: oil.available }];
      var selected = variants[0];

      var html = '<div class="aq-product-card' + (isTopPick ? " is-top-pick" : "") + '" data-aq-card>';
      html += '<span class="aq-product-tag' + (isTopPick ? "" : " is-alt") + '">' +
        esc(isTopPick ? ((copy && copy.bestMatch) || "Best Match") : ((copy && copy.alsoRecommended) || "Also Recommended")) + "</span>";
      html += '<div class="aq-product-media">';
      html += oil.image ? '<img src="' + esc(oil.image) + '" alt="' + esc(oil.title) + '" loading="lazy">' : placeholderMediaHtml();
      html += "</div>";
      html += '<div class="aq-product-body">';
      html += '<h4 class="aq-product-name">' + esc(oil.title) + "</h4>";

      // When a card comes from outside the winning family, say so -- otherwise
      // it reads as a mistake rather than a deliberate extra suggestion.
      if (entry.crossFamily && entry.family && data.families[entry.family]) {
        html += '<p class="aq-product-note">From the ' + esc(data.families[entry.family].name) + " family</p>";
      }

      if (variants.length > 1) {
        html += '<div class="aq-size-pills">';
        variants.forEach(function (v, i) {
          html += '<button type="button" class="aq-size-pill' + (i === 0 ? " is-selected" : "") + '" data-variant-index="' + i + '" data-available="' + (v.available ? "true" : "false") + '">' + esc(v.label) + "</button>";
        });
        html += "</div>";
      }

      html += '<p class="aq-product-price">' + (selected.price || "") + "</p>";
      html += '<div class="aq-product-actions">';
      if (selected.available && selected.variantId) {
        html += '<button type="button" class="aq-btn aq-btn-primary aq-btn-block" data-aq-add-single="' + esc(selected.variantId) + '">' + esc((copy && copy.addToCart) || "Add to Cart") + "</button>";
      }
      if (oil.url) {
        html += '<a class="aq-btn aq-btn-ghost aq-btn-block" href="' + esc(oil.url) + '">' + esc((copy && copy.viewProduct) || "View Product") + "</a>";
      }
      html += "</div></div></div>";
      return html;
    }

    // Every diffuser the merchant configured -- not just the ones shown above,
    // so a customer who wants the gift with a diffuser the quiz didn't
    // recommend can still get to it from here.
    //
    // Opt-OUT, not opt-in. This used to require a per-block checkbox, which
    // meant the whole block stayed invisible on a section built before that
    // checkbox existed, with the merchant given no reason why. The offer is
    // the point of the block, so it is on by default and a diffuser has to be
    // deliberately taken out.
    function eligibleDiffusers() {
      return (data.diffusers || []).filter(function (d) {
        return d && !d.bundleHide && d.product && d.product.available && d.product.variantId;
      });
    }

    function bundleHtml(results, copy) {
      var g = data.gift || {};
      var b = data.bundle || {};
      if (b.enabled === false || g.enabled === false) return "";

      var oils = availableGifts();
      var diffs = eligibleDiffusers();
      // Nothing to pair, or nothing to pair it with: the block would be an
      // empty promise, so it doesn't render for customers. It DOES leave a
      // notice in the theme editor saying which piece is missing -- silently
      // vanishing is how a misconfigured block goes unnoticed for weeks.
      if (!oils.length || !diffs.length) return bundleSetupNoticeHtml(oils, diffs);

      bundle.diffusers = diffs;
      bundle.oils = oils;
      bundle.d = null;
      bundle.o = null;

      // Pre-select the diffuser the quiz just recommended, when it qualifies --
      // it's the one the customer already has in mind. The OIL is left
      // unselected on purpose: choosing it is the point of the block.
      var rec = results.diffuser && results.diffuser.block;
      if (rec && !rec.bundleHide && rec.product) {
        diffs.forEach(function (d, i) {
          if (d.product.variantId === rec.product.variantId) bundle.d = i;
        });
      }

      var html = '<div class="aq-bundle-wrap" data-aq-bundle>';
      if (b.sectionTitle) html += '<h4 class="aq-result-section-title">' + esc(b.sectionTitle) + "</h4>";
      if (b.sectionSub) html += '<p class="aq-result-section-sub">' + esc(b.sectionSub) + "</p>";

      html += '<div class="aq-bundle">';

      html += '<div class="aq-bundle-head">';
      html += '<span class="aq-bundle-badge">' + giftSvg(13) + esc(g.badge || "Gift Included") + "</span>";
      html += '<h3 class="aq-bundle-title">' + esc(b.heading || "Would you like a gift oil with your diffuser?") + "</h3>";
      if (b.body) html += '<p class="aq-bundle-sub">' + esc(b.body) + "</p>";
      html += "</div>";

      // ---- step 1: the diffuser
      html += '<div class="aq-bundle-step" data-aq-bundle-step="1">';
      html += bundleStepHead(1, b.step1 || "Choose your diffuser", b.step1Hint || "Pick one");
      html += '<div class="aq-bundle-tiles" role="radiogroup" aria-label="' + esc(b.step1 || "Choose your diffuser") + '">';
      diffs.forEach(function (d, i) {
        var sel = bundle.d === i;
        var name = diffuserDisplayName(d.product.title);
        html += '<button type="button" role="radio" aria-checked="' + (sel ? "true" : "false") + '"' +
          ' class="aq-bundle-tile' + (sel ? " is-selected" : "") + '" data-aq-bundle-diffuser="' + i + '">';
        html += '<span class="aq-bundle-tick">' + checkSvg(13) + "</span>";
        html += '<span class="aq-bundle-tile-media">';
        html += d.product.image
          ? '<img src="' + esc(d.product.image) + '" alt="' + esc(name) + '" loading="lazy">'
          : placeholderMediaHtml();
        html += "</span>";
        html += '<span class="aq-bundle-tile-body">';
        html += '<span class="aq-bundle-tile-name">' + esc(name) + "</span>";
        if (d.note) html += '<span class="aq-bundle-tile-desc">' + esc(d.note) + "</span>";
        if (d.product.price) html += '<span class="aq-bundle-tile-price">' + d.product.price + "</span>";
        html += "</span></button>";
      });
      html += "</div></div>";

      // ---- step 2: the oil. Locked until a diffuser is chosen, so there is
      // exactly one obvious next move at any moment.
      html += '<div class="aq-bundle-step' + (bundle.d === null ? " is-locked" : "") + '" data-aq-bundle-step="2">';
      html += bundleStepHead(2, b.step2 || "Choose your gift oil", b.step2Hint || "");
      html += '<div class="aq-gift-options" role="radiogroup" aria-label="' + esc(b.step2 || "Choose your gift oil") + '">';
      oils.forEach(function (oil, i) {
        html += '<button type="button" role="radio" aria-checked="false" class="aq-gift-option" data-aq-bundle-oil="' + i + '">';
        html += '<span class="aq-gift-thumb">';
        html += oil.image ? '<img src="' + esc(oil.image) + '" alt="' + esc(oil.label || oil.title) + '" loading="lazy">' : "";
        html += '<span class="aq-gift-check">' + checkSvg(11) + "</span>";
        html += "</span>";
        html += '<span class="aq-gift-name">' + esc(oil.label || oil.title) + "</span>";
        if (oil.variantLabel) html += '<span class="aq-gift-size">' + esc(oil.variantLabel) + "</span>";
        html += "</button>";
      });
      html += "</div>";
      if (g.note) html += '<p class="aq-gift-note">' + esc(g.note) + "</p>";
      html += "</div>";

      // ---- summary
      html += '<div class="aq-bundle-summary">';
      html += '<div class="aq-bundle-summary-text">';
      html += '<div class="aq-bundle-summary-line" data-aq-bundle-line></div>';
      html += '<div class="aq-bundle-summary-meta" data-aq-bundle-meta></div>';
      html += "</div>";
      html += '<button type="button" class="aq-btn aq-btn-primary" data-aq-bundle-add disabled>' +
        esc(b.button || "Add Bundle to Cart") + "</button>";
      html += "</div>";

      html += "</div></div>";
      return html;
    }

    // True in Shopify's theme editor, or on any URL carrying ?aq_debug=1.
    // Customers on the live storefront never hit either.
    function isSetupView() {
      try {
        if (window.Shopify && window.Shopify.designMode) return true;
        return String(window.location.search || "").indexOf("aq_debug") !== -1;
      } catch (e) { return false; }
    }

    function bundleSetupNoticeHtml(oils, diffs) {
      if (!isSetupView()) return "";
      var c = data.counts || {};
      var msgs = [];

      if (!diffs.length) {
        msgs.push(!c.diffuserBlocks
          ? "This section has no \u201CDiffuser recommendation\u201D blocks, so there is no diffuser to pair the gift with."
          : "All " + c.diffuserBlocks + " diffuser block(s) are either ticked \u201CHide from the gift oil bundle\u201D, missing a product, or sold out.");
      }

      if (!oils.length) {
        if (!c.giftBlocks) {
          msgs.push("There are no \u201CFree gift oil\u201D blocks \u2014 add one per oil the customer can choose from.");
        } else if (!c.giftBlocksWithProduct) {
          msgs.push("All " + c.giftBlocks + " \u201CFree gift oil\u201D block(s) are missing a product. Open each one and pick the aroma oil \u2014 a label on its own isn't enough.");
        } else {
          msgs.push("The gift oil blocks have products, but none has an available variant matching the \u201CGift size keyword\u201D (currently \u201C" +
            (c.giftSizeKeyword || "150") + "\u201D). Check that against the variant names, and that those variants are in stock.");
        }
      }

      return '<div class="aq-config-warning aq-bundle-warning"><b>Gift oil bundle block is hidden.</b> ' +
        esc(msgs.join(" ")) +
        " This notice shows only in the theme editor and on ?aq_debug=1 \u2014 customers never see it.</div>";
    }

    function bundleStepHead(num, title, hint) {
      var html = '<div class="aq-bundle-step-head">';
      html += '<span class="aq-bundle-step-num">' + num + "</span>";
      html += '<p class="aq-bundle-step-title">' + esc(title) + "</p>";
      if (hint) html += '<p class="aq-bundle-step-hint">' + esc(hint) + "</p>";
      html += "</div>";
      return html;
    }

    function renderBundleSummary() {
      var root = els.resultInner.querySelector("[data-aq-bundle]");
      if (!root) return;
      var b = data.bundle || {};
      var g = data.gift || {};
      var line = root.querySelector("[data-aq-bundle-line]");
      var meta = root.querySelector("[data-aq-bundle-meta]");
      var btn = root.querySelector("[data-aq-bundle-add]");
      var d = bundle.d === null ? null : bundle.diffusers[bundle.d];
      var o = bundle.o === null ? null : bundle.oils[bundle.o];

      if (!d) {
        line.innerHTML = '<span class="is-muted">' + esc(b.emptyLine || "Pick a diffuser to begin") + "</span>";
        meta.textContent = b.emptyMeta || "";
        btn.disabled = true;
        return;
      }

      var dName = diffuserDisplayName(d.product.title);
      var oName = o ? ((o.label || o.title) + (o.variantLabel ? " " + o.variantLabel : "")) : "";

      if (!o) {
        line.textContent = dName;
        meta.innerHTML = (d.product.price || "") + ' &nbsp;\u00b7&nbsp; ' + esc(b.chooseOilLine || "Choose your gift oil above");
        btn.disabled = true;
        return;
      }

      line.textContent = dName + " + " + oName;
      var worth = g.value ? ", " + g.value : "";
      meta.innerHTML = (d.product.price || "") + ' &nbsp;\u00b7&nbsp; <b>' +
        esc(oName + " " + (b.giftSuffix || "is your gift") + worth) + "</b>";
      btn.disabled = false;
    }

    function wireBundle(copy) {
      var root = els.resultInner.querySelector("[data-aq-bundle]");
      if (!root) return;

      function syncGroup(nodes, activeIndex) {
        Array.prototype.forEach.call(nodes, function (n) {
          var on = Number(n.getAttribute(n.hasAttribute("data-aq-bundle-diffuser") ? "data-aq-bundle-diffuser" : "data-aq-bundle-oil")) === activeIndex;
          n.classList.toggle("is-selected", on);
          n.setAttribute("aria-checked", on ? "true" : "false");
        });
      }

      var dNodes = root.querySelectorAll("[data-aq-bundle-diffuser]");
      var oNodes = root.querySelectorAll("[data-aq-bundle-oil]");
      var step2 = root.querySelector('[data-aq-bundle-step="2"]');

      Array.prototype.forEach.call(dNodes, function (n) {
        n.addEventListener("click", function () {
          bundle.d = Number(n.getAttribute("data-aq-bundle-diffuser"));
          syncGroup(dNodes, bundle.d);
          if (step2) step2.classList.remove("is-locked");
          renderBundleSummary();
        });
      });

      Array.prototype.forEach.call(oNodes, function (n) {
        n.addEventListener("click", function () {
          bundle.o = Number(n.getAttribute("data-aq-bundle-oil"));
          syncGroup(oNodes, bundle.o);
          renderBundleSummary();
        });
      });

      var addBtn = root.querySelector("[data-aq-bundle-add]");
      if (addBtn) {
        addBtn.addEventListener("click", function () {
          var d = bundle.d === null ? null : bundle.diffusers[bundle.d];
          var o = bundle.o === null ? null : bundle.oils[bundle.o];
          if (!d || !o) return;
          var giftLabel = (data.gift && data.gift.lineItemLabel) || "Gift Oil";
          var primary = { id: Number(d.product.variantId), quantity: 1, properties: {} };
          primary.properties[giftLabel] = (o.label || o.title) + (o.variantLabel ? " " + o.variantLabel : "");
          var giftItem = {
            id: Number(o.variantId),
            quantity: 1,
            properties: {
              "_free_gift": "true",
              "_gift_with": d.product.title,
              "Gift with": d.product.title
            }
          };
          addItemsToCart([primary], addBtn, giftItem);
        });
      }

      renderBundleSummary();
    }

    // `entry` is a scored diffuser: { block, matched } -- `matched` holding the
    // labels of the answers that put this diffuser in front of the customer.
    // isPrimary: the hero "Complete Your Ritual" card. Alternatives render
    // with the same markup so the gift picker behaves identically on each.
    function diffuserCardHtml(entry, copy, isPrimary) {
      var diffuserBlock = entry ? entry.block : null;
      var product = diffuserBlock ? diffuserBlock.product : null;
      if (!product) {
        return isPrimary
          ? '<div class="aq-empty-note">' + esc("A diffuser pairing will appear here once a product is connected to a block in the theme editor.") + "</div>"
          : "";
      }

      var html = '<div class="aq-diffuser-card' + (isPrimary ? " is-primary" : " is-alt") + '">';

      html += '<div class="aq-product-media">';
      html += product.image ? '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" loading="lazy">' : placeholderMediaHtml();
      html += "</div>";

      html += '<div class="aq-product-body">';
      html += '<h4 class="aq-product-name">' + esc(diffuserDisplayName(product.title)) + "</h4>";
      if (diffuserBlock.note) html += '<p class="aq-product-note">' + esc(diffuserBlock.note) + "</p>";
      if (product.price) html += '<p class="aq-product-price">' + product.price + "</p>";

      var why = diffuserReasonText(entry, copy);
      if (why) {
        html += '<div class="aq-diffuser-why">';
        html += '<span class="aq-diffuser-why-label">' + esc((copy && copy.whyLabel) || "Why this one") + "</span>";
        html += '<p class="aq-diffuser-why-text">' + esc(why) + "</p>";
        html += "</div>";
      }

      html += "</div>";

      html += '<div class="aq-diffuser-actions">';
      if (product.available && product.variantId) {
        html += '<button type="button" class="aq-btn aq-btn-primary" data-aq-add-diffuser="' + esc(product.variantId) + '" data-diffuser-title="' + esc(product.title) + '">' +
          esc((copy && copy.addToCart) || "Add to Cart") + "</button>";
      }
      if (product.url) {
        html += '<a class="aq-btn aq-btn-ghost" href="' + esc(product.url) + '">' + esc((copy && copy.viewProduct) || "View Product") + "</a>";
      }
      html += "</div>";

      html += "</div>";
      return html;
    }

    // ---------------------------------------------------------------------
    // Cart
    // ---------------------------------------------------------------------
    function cartRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : "/";
    }

    // The gift is added as a SEPARATE request after the main item, on purpose:
    // /cart/add.js is all-or-nothing, so bundling them would mean a
    // sold-out gift oil silently blocks the diffuser sale too. This way the
    // diffuser always lands in the cart and the gift is a best-effort extra.
    function addItemsToCart(items, btn, giftItem) {
      if (!items || !items.length) return;
      setBtnLoading(btn, true);

      postCart(items)
        .then(function () {
          if (!giftItem) return null;
          return postCart([giftItem]).catch(function () { return null; });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          setBtnLoading(btn, false, "Added ✓");
          var code = data.gift && data.gift.discountCode;
          if (giftItem && code) {
            // Discount CODE flow only. With an automatic Buy X Get Y discount
            // (recommended) this setting stays blank and nothing redirects.
            window.location.href = cartRoot() + "discount/" + encodeURIComponent(code) +
              "?redirect=" + encodeURIComponent(cartRoot() + "cart");
          }
        })
        .catch(function () {
          setBtnLoading(btn, false, "Couldn't add — try again");
        });
    }

    function postCart(items) {
      return fetch(cartRoot() + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error((json && json.description) || "cart error");
          return json;
        });
      });
    }

    function setBtnLoading(btn, loading, doneText) {
      if (!btn) return;
      if (loading) {
        if (!btn.dataset.label) btn.dataset.label = btn.textContent;
        btn.textContent = "Adding...";
        btn.disabled = true;
      } else {
        btn.textContent = doneText || btn.dataset.label || btn.textContent;
        btn.disabled = false;
      }
    }

    function esc(str) {
      if (str === null || str === undefined) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    function diffuserDisplayName(title) {
      return (title || "").replace(/\bdiffuser\b/gi, "").replace(/\s{2,}/g, " ").trim();
    }

    // Initial paint
    renderQuestion();
    showScreen("intro");
  }

  function boot() {
    // querySelectorAll, not querySelector: initialize every quiz section on
    // the page, not just the first one. Each section's data was written to
    // window.aromaQuizData[section.id] (see the <script> block in
    // sc-quiz-v2.liquid), so two instances on one page -- e.g. this version
    // running next to another one for comparison -- no longer collide over
    // a single shared "window.quizData" and no longer leave every instance
    // after the first with a Start button that has no click handler at all.
    var roots = document.querySelectorAll(".aroma-quiz-root");
    if (!roots.length) return;
    var store = window.aromaQuizData || {};
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var sectionId = root.getAttribute("data-section-id");
      init(root, store[sectionId]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();